interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ListPaginationProps {
  pagination?: Pagination;
  page: number;
  rowsShown: number;
  itemLabel?: string;
  onPageChange: (page: number) => void;
  limit?: number;
  onLimitChange?: (limit: number) => void;
  pageSizeOptions?: number[];
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const buildPageList = (current: number, total: number): Array<number | "…"> => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: Array<number | "…"> = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) pages.push("…");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push("…");
  pages.push(total);
  return pages;
};

export const ListPagination = ({
  pagination,
  page,
  rowsShown,
  itemLabel = "records",
  onPageChange,
  limit,
  onLimitChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: ListPaginationProps) => {
  const total = pagination?.total ?? rowsShown;
  const currentLimit = limit ?? pagination?.limit;
  const totalPages = pagination?.totalPages ?? 1;
  const currentPage = Math.min(
    Math.max(1, pagination?.page ?? page),
    Math.max(1, totalPages),
  );
  const showPager = totalPages > 1;
  const showLimit = !!onLimitChange && currentLimit != null;
  const perPage = currentLimit ?? rowsShown;
  const start = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const end = total === 0 ? 0 : start + rowsShown - 1;
  const pageList = buildPageList(currentPage, totalPages);

  const go = (p: number) => {
    const clamped = Math.min(Math.max(1, p), totalPages);
    if (clamped !== currentPage) onPageChange(clamped);
  };

  return (
    <div className="px-4 py-3 border-t border-border flex items-center justify-between text-sm text-muted-foreground gap-3 flex-wrap">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="whitespace-nowrap">
          {total === 0
            ? `0 ${itemLabel}`
            : `Showing ${start}–${end} of ${total} ${itemLabel}`}
        </span>
        {showLimit && (
          <label className="flex items-center gap-2">
            <span className="hidden sm:inline">Rows per page</span>
            <select
              value={currentLimit}
              onChange={(e) => onLimitChange?.(Number(e.target.value))}
              className="rounded-btn border border-border bg-background px-2 py-1 text-foreground"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {showPager && (
        <nav
          className="flex items-center gap-1 flex-wrap"
          aria-label="Pagination"
        >
          <button
            type="button"
            onClick={() => go(1)}
            disabled={currentPage <= 1}
            className="px-2 py-1 rounded-btn border border-border disabled:opacity-50 hover:bg-muted"
            aria-label="First page"
          >
            «
          </button>
          <button
            type="button"
            onClick={() => go(currentPage - 1)}
            disabled={currentPage <= 1}
            className="px-3 py-1 rounded-btn border border-border disabled:opacity-50 hover:bg-muted"
            aria-label="Previous page"
          >
            Prev
          </button>
          {pageList.map((p, i) =>
            p === "…" ? (
              <span key={`e-${i}`} className="px-2 select-none" aria-hidden>
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => go(p)}
                aria-current={p === currentPage ? "page" : undefined}
                className={`min-w-[2rem] px-2 py-1 rounded-btn border ${
                  p === currentPage
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-muted"
                }`}
              >
                {p}
              </button>
            ),
          )}
          <button
            type="button"
            onClick={() => go(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="px-3 py-1 rounded-btn border border-border disabled:opacity-50 hover:bg-muted"
            aria-label="Next page"
          >
            Next
          </button>
          <button
            type="button"
            onClick={() => go(totalPages)}
            disabled={currentPage >= totalPages}
            className="px-2 py-1 rounded-btn border border-border disabled:opacity-50 hover:bg-muted"
            aria-label="Last page"
          >
            »
          </button>
        </nav>
      )}
    </div>
  );
};

export default ListPagination;
